import { useEffect, useRef, useState } from 'react';

/**
 * Soft typing when nearly caught up.
 * Tuned to Ant Design XMarkdown streaming demos (~30–50ms/char ≈ 20–33 cps).
 * format.tsx uses 30ms/char; combined.tsx uses 50ms/char.
 */
export const STREAMING_REVEAL_BASE_CPS = 33;
/** Mild backlog catch-up — keep dumps readable, never 5k-cps blur. */
export const STREAMING_REVEAL_CATCHUP_PER_BACKLOG = 2;
export const STREAMING_REVEAL_CATCHUP_CAP_CPS = 280;
/** Budget for kickstart / cold-start frames (≈ one display refresh). */
export const STREAMING_REVEAL_FRAME_MS = 16;

/**
 * How many characters to reveal this frame.
 * AntDX-like base cps + soft backlog catch-up; flush snaps to target.
 */
export function streamingRevealStep(
  displayedLen: number,
  targetLen: number,
  dtMs: number,
  flushing: boolean,
): number {
  const backlog = targetLen - displayedLen;
  if (backlog <= 0) return 0;
  if (flushing) return backlog;

  const catchupCps = Math.min(
    STREAMING_REVEAL_CATCHUP_CAP_CPS,
    backlog * STREAMING_REVEAL_CATCHUP_PER_BACKLOG,
  );
  const cps = STREAMING_REVEAL_BASE_CPS + catchupCps;
  return Math.max(1, Math.ceil((cps * Math.max(dtMs, 1)) / 1000));
}

/** Advance displayed toward target by `chars`, repairing divergence via common prefix. */
export function nextStreamingReveal(displayed: string, target: string, chars: number): string {
  if (!target) return '';

  let prefix = displayed;
  if (!target.startsWith(prefix)) {
    let i = 0;
    const max = Math.min(prefix.length, target.length);
    while (i < max && prefix.charCodeAt(i) === target.charCodeAt(i)) i += 1;
    prefix = target.slice(0, i);
  }

  if (chars <= 0) return prefix;
  return target.slice(0, Math.min(prefix.length + chars, target.length));
}

function isBehindTarget(displayed: string, target: string): boolean {
  return displayed.length < target.length || !target.startsWith(displayed);
}

/**
 * Display layer for assistant text: SignalR / refresh content is the target;
 * UI interpolates toward it so hub chunks and complete-only replies never jump.
 *
 * `active` only seeds cold start (Pending → begin empty). When active flips false
 * (finalize Done) while still behind, soft typewriter continues from current
 * length — no replay from scratch, no hard snap (streamed-then-complete safe).
 * History Done mounts inactive with full target → shows full immediately.
 */
export function useStreamingReveal(target: string, active: boolean): string {
  const [displayed, setDisplayed] = useState(() => (active ? '' : target));
  const displayedRef = useRef(displayed);
  const targetRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  displayedRef.current = displayed;
  targetRef.current = target;

  useEffect(() => {
    const stopRaf = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTsRef.current = null;
    };

    const applyStep = (dtMs: number) => {
      const current = displayedRef.current;
      const goal = targetRef.current;
      const step = streamingRevealStep(current.length, goal.length, dtMs, false);
      const next = nextStreamingReveal(current, goal, step);
      if (next !== current) {
        displayedRef.current = next;
        setDisplayed(next);
      }
      return next;
    };

    const tick = (ts: number) => {
      const cold = lastTsRef.current == null;
      const prevTs = lastTsRef.current ?? ts;
      lastTsRef.current = ts;
      const rawDt = Math.min(64, Math.max(0, ts - prevTs));
      const dt = cold ? Math.max(rawDt, STREAMING_REVEAL_FRAME_MS) : rawDt;

      const next = applyStep(dt);
      if (isBehindTarget(next, targetRef.current)) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (next !== targetRef.current) {
        displayedRef.current = targetRef.current;
        setDisplayed(targetRef.current);
      }
      rafRef.current = null;
      lastTsRef.current = null;
    };

    if (!isBehindTarget(displayedRef.current, target)) {
      stopRaf();
      if (displayedRef.current !== target) {
        displayedRef.current = target;
        setDisplayed(target);
      }
      return;
    }

    if (rafRef.current == null) {
      applyStep(STREAMING_REVEAL_FRAME_MS);
      lastTsRef.current = null;
    }

    if (isBehindTarget(displayedRef.current, targetRef.current) && rafRef.current == null) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [target, active]);

  useEffect(
    () => () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    [],
  );

  return displayed;
}
